import { api } from './client';

export interface AdminStats {
  total_products: number;
  active_products: number;
  inactive_products: number;
  total_users: number;
  buyer_users: number;
  reseller_users: number;
  pending_users: number;
  pending_reseller_applications: number;
  pending_quote_requests: number;
  unread_contact_messages: number;
  orders_today: number;
  revenue_this_month: number;
  recent_orders: any[];
  recent_quotes: any[];
  recent_applications: any[];
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface ProductInput {
  category_id: number;
  subcategory_id: number;
  brand: string;
  ref: string;
  name: string;
  name_en: string;
  description?: string;
  description_en?: string;
  price: number;
  reseller_price?: number | null;
  stock: number;
  moq: number;
  quote_threshold: number;
  features: string[];
  features_en: string[];
  images: string[];
  variants: { sizes?: string[]; colors?: string[] };
  norm_ids: number[];
  sector_ids: number[];
  bestseller: boolean;
  badge?: string;
  badge_en?: string;
  is_active: boolean;
}

export interface UserInput {
  full_name: string;
  email: string;
  company?: string;
  phone?: string;
  role: 'buyer' | 'reseller' | 'super_admin';
  status: 'active' | 'pending' | 'suspended';
}

export interface CategoryData {
  id?: number;
  slug: string;
  name_fr: string;
  name_en: string;
}

export interface SubcategoryData {
  id?: number;
  slug: string;
  name_fr: string;
  name_en: string;
  category_id: number;
}

export interface CategoryWithSubcategories {
  id: number;
  slug: string;
  name_fr: string;
  name_en: string;
  subcategories: SubcategoryData[];
}

export const adminApi = {
  // Categories
  getCategories: () =>
    api.get<CategoryWithSubcategories[]>('/admin/categories/'),

  createCategory: (data: CategoryData) =>
    api.post<{ id: number; message: string }>('/admin/categories/', data),

  updateCategory: (id: number, data: CategoryData) =>
    api.patch<{ id: number; message: string }>(`/admin/categories/${id}/`, data),

  deleteCategory: (id: number) =>
    api.delete<{ message: string }>(`/admin/categories/${id}/`),

  // Subcategories
  getSubcategories: (categoryId?: number) =>
    api.get<SubcategoryData[]>('/admin/subcategories/', categoryId ? { category_id: categoryId } : {}),

  createSubcategory: (data: SubcategoryData) =>
    api.post<{ id: number; message: string }>('/admin/subcategories/', data),

  updateSubcategory: (id: number, data: SubcategoryData) =>
    api.patch<{ id: number; message: string }>(`/admin/subcategories/${id}/`, data),

  deleteSubcategory: (id: number) =>
    api.delete<{ message: string }>(`/admin/subcategories/${id}/`),

  getStats: () =>
    api.get<AdminStats>('/admin/stats/'),

  getProducts: (params?: { page?: number; limit?: number; search?: string; category?: string; is_active?: boolean }) =>
    api.get<PaginatedResponse<any>>('/admin/products/', params),

  createProduct: (data: ProductInput) =>
    api.post<any>('/admin/products/', data),

  updateProduct: (id: number, data: ProductInput) =>
    api.patch<any>(`/admin/products/${id}/`, data),

  deleteProduct: (id: number) =>
    api.delete<{ message: string }>(`/admin/products/${id}/`),

  importProductsCsv: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ created: number; updated: number; errors: any[]; total_rows: number }>(
      '/admin/products/import-csv/',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  downloadCsvTemplate: async (): Promise<void> => {
    const API_BASE = (import.meta as any).env?.VITE_API_URL || '/api/v1';
    const token = localStorage.getItem('lodiss_auth_token');
    const res = await fetch(`${API_BASE}/admin/products/csv-template/`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${JSON.parse(token)}` } : {},
    });
    if (!res.ok) throw new Error('Template download failed');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lodiss_products_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  getUsers: (params?: { page?: number; limit?: number; search?: string; role?: string; status?: string }) =>
    api.get<PaginatedResponse<any>>('/admin/users/', params),

  updateUser: (id: number, data: Partial<UserInput>) =>
    api.patch<any>(`/admin/users/${id}/`, data),

  resetUserPassword: (id: number) =>
    api.post<{ message: string }>(`/admin/users/${id}/reset-password/`),

  deleteUser: (id: number) =>
    api.delete<{ message: string }>(`/admin/users/${id}/`),

  getResellerApplications: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get<PaginatedResponse<any>>('/admin/reseller-applications/', params),

  getResellerApplication: (id: number) =>
    api.get<any>(`/admin/reseller-applications/${id}/`),

  updateResellerApplication: (id: number, data: { status: string; admin_notes?: string }) =>
    api.patch<any>(`/admin/reseller-applications/${id}/`, data),

  resendActivation: (id: number) =>
    api.post<{ success: boolean; sent_to?: string; expires_at?: string; error?: string }>(`/admin/reseller-applications/${id}/resend-activation/`),

  getOrders: (params?: { page?: number; limit?: number; search?: string; status?: string; type?: string }) =>
    api.get<PaginatedResponse<any>>('/admin/orders/', params),

  getOrder: (id: number) =>
    api.get<any>(`/admin/orders/${id}/`),

  updateOrderStatus: (id: number, data: { status: string; tracking_number?: string }) =>
    api.patch<any>(`/admin/orders/${id}/status/`, data),

  getQuotes: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get<PaginatedResponse<any>>('/admin/quotes/', params),

  getQuote: (id: number) =>
    api.get<any>(`/admin/quotes/${id}/`),

  updateQuote: (id: number, data: { status: string; admin_notes?: string; items?: any[] }) =>
    api.patch<any>(`/admin/quotes/${id}/`, data),

  getContactMessages: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get<PaginatedResponse<any>>('/admin/contact-messages/', params),

  getContactMessage: (id: number) =>
    api.get<any>(`/admin/contact-messages/${id}/`),

  updateContactMessage: (id: number, data: { status: string }) =>
    api.patch<any>(`/admin/contact-messages/${id}/`, data),

  deleteContactMessage: (id: number) =>
    api.delete<{ message: string }>(`/admin/contact-messages/${id}/`),

  uploadProductImages: (productId: number, files: File[]) =>
    api.uploadFiles<{ urls: string[]; all_images: string[] }>(`/admin/products/${productId}/images/`, files),

  deleteProductImage: (productId: number, url: string) =>
    api.delete<{ message: string; all_images: string[] }>(`/admin/products/${productId}/images/?url=${encodeURIComponent(url)}`),

  downloadQuotePdf: async (quoteId: number): Promise<{ blob: Blob; filename: string }> => {
    const API_BASE = (import.meta as any).env?.VITE_API_URL || '/api/v1';
    const token = api.getToken();
    const res = await fetch(`${API_BASE}/admin/quotes/${quoteId}/pdf/`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`PDF fetch failed: ${res.status}`);
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `Lodiss_Devis_#${quoteId}.pdf`;
    return { blob, filename };
  },

  resendQuoteEmail: (quoteId: number) =>
    api.post<{ success: boolean; sent_to?: string; error?: string }>(`/admin/quotes/${quoteId}/resend-email/`),
};

